import * as amplitude from "@amplitude/analytics-browser";
import axios from "axios";
import qs from "query-string";
import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useBankFeeds } from "../../components/bankFeeds/bankFeedsAuthProvider";
import { BankFeedsSupportedRutterPlatforms } from "../../components/bankFeeds/constants";
import { DataSharing } from "../../components/bankFeeds/dataSharing";
import { DefaultLogin } from "../../components/bankFeeds/defaultLogin";
import BankFeedsCommonLayout, {
  LOCAL_STORAGE_ORG_INFO,
  OrgInfo,
} from "../../components/bankFeeds/layout";
import { SecureConnection } from "../../components/bankFeeds/secureConnection";
import { getBackendUrl } from "../../utils";

type SearchParams = {
  challenge: string;
  name: string;
  companyLogoUrl: string;
  otpInstructions: string;
  errorMessage?: string;
};

const SageBankFeedsAuth: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string>("");

  return (
    <BankFeedsCommonLayout errorMessage={errorMessage}>
      <SageBankFeedsAuthComponent setErrorMessage={setErrorMessage} />
    </BankFeedsCommonLayout>
  );
};

function SageBankFeedsAuthComponent({
  setErrorMessage,
}: {
  setErrorMessage: (value: string) => void;
}) {
  const history = useHistory();
  const { search } = history.location;
  const { challenge, errorMessage } = qs.parse(search) as SearchParams;

  const {
    setOrgInfo,
    setShowDefaultLogin,
    setIsSubmitting,
    setIsFetchingOrgInfo,
    setIsRedirecting,
    setShowInstitutionMessage,
    orgInfo,
    password,
    isRedirecting,
    isFetchingOrgInfo,
    showDefaultLogin,
  } = useBankFeeds();

  useEffect(() => {
    axios
      .get(
        `${getBackendUrl()}/sageBankFeedsConnector/auth/org?login_challenge=${challenge}`,
      )
      .then((response) => {
        if (typeof response.data !== "object") {
          setOrgInfo({ orgId: response.data });
        } else {
          const {
            orgId,
            companyLogoUrl,
            otpInstructions,
            name,
            redirectUrl,
            termsAndConditions,
          } = response.data;
          const orgInfo: OrgInfo = {
            orgId,
            companyLogoUrl,
            otpInstructions,
            name,
            redirectUrl,
            termsAndConditions,
            platform: BankFeedsSupportedRutterPlatforms.SAGE_BANK_FEEDS,
          };

          setOrgInfo(orgInfo);
          localStorage.setItem(LOCAL_STORAGE_ORG_INFO, JSON.stringify(orgInfo));
          amplitude.track("Sage Bank Feeds: Auth Page Loaded", {
            orgId,
          });
        }
      })
      .catch((err) => {
        amplitude.track("Sage Bank Feeds: Org Fetch Failed");
        history.push("/sage_bank_feeds_error");
      })
      .finally(() => {
        setIsFetchingOrgInfo(false);
        setErrorMessage(errorMessage ?? "");
      });
  }, [challenge, setOrgInfo, setIsFetchingOrgInfo]);

  const handleCompleteSubmit = () => {
    amplitude.track("Sage Bank Feeds: Auth Submit Clicked");
    const redirectUriWithPassword = `/sage_bank_feeds_redirect?challenge=${challenge}&otp=${password}`;
    window.location.replace(redirectUriWithPassword);
  };

  const handleCompleteCancel = () => {
    amplitude.track("sage Bank Feeds: Auth Cancel Clicked");
    history.push({
      pathname: "/bank_feeds_cancelled",
      state: { orgInfo },
    });
  };

  const continueToLoginHandler = () => {
    if (!orgInfo.redirectUrl) return setShowDefaultLogin(true);

    setIsRedirecting(true);
    setShowInstitutionMessage(true);
    window.location.replace(`${orgInfo.redirectUrl}`); // redirectUrl will already have the challenge at this point.
  };

  return isRedirecting || isFetchingOrgInfo ? (
    <SecureConnection />
  ) : showDefaultLogin ? (
    <DefaultLogin
      handleCompleteCancel={handleCompleteCancel}
      handleCompleteSubmit={handleCompleteSubmit}
      platform={BankFeedsSupportedRutterPlatforms.SAGE_BANK_FEEDS}
    />
  ) : (
    <DataSharing
      cancelHandler={handleCompleteCancel}
      continueToLoginHandler={continueToLoginHandler}
    />
  );
}

export default SageBankFeedsAuth;
